<template>
  <div
    ref="wrapperRef"
    class="wrapper position-relative align-center d-flex flex-column pb-lg-3 position-relative"
    data-elbcontext="shopping:discover"
    data-elb="product"
    :data-elb-product="elbData"
  >
    <div :style="background" class="bg-wrapper"></div>
    <div
      class="v-container px-0 pt-0 pt-md-5 pt-lg-2 d-flex flex-column flex-sm-column-reverse flex-lg-row flex-grow-1 position-relative"
    >
      <div v-if="coop" class="coop bg-white py-2 px-3 mx-md-3 d-flex">
        <img :src="coop" :alt="translate('opc_coop_img_alt')" />
      </div>
      <div class="v-col-12 v-col-lg-7 mx-auto px-0">
        <div class="h-100 w-100 position-relative d-flex justify-center">
          <ProductImage
            class="px-5 px-lg-0 w-100"
            :product-id="product.product_id"
            :campaign-id="product.campaign_id"
            :height="imageHeight"
            :width="imageHeight"
            :alt="product.name"
            :is-bundle="isBundle"
            use-hero-image
            add-item-prop
          />
          <TheOpcAwards
            v-if="badges.length"
            :class="[
              'award-list',
              isBundle ? 'pt-3' : 'pt-5',
              'pt-sm-3',
              !isBundle ? 'pt-lg-7' : 'pt-lg-3',
              'pr-3',
              'pr-md-5',
              'pr-lg-3'
            ]"
            :awards="badges"
            :threshold="awardThreshold"
            placement="left"
          />
        </div>
      </div>
      <div
        v-if="smAndUp"
        class="stage-info v-col-12 v-col-lg-5 mx-auto px-0 align-self-center"
      >
        <TheOpcStageInfo class="px-3 pb-0 pb-sm-2" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { translate } from '@/plugins/i18n.js'
import ElbwalkerHelper from '@/services/helpers/ElbwalkerHelper'
import { useOpcStore } from '@/stores/opc'
import { storeToRefs } from 'pinia'
import { computed, defineAsyncComponent, nextTick, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'

const ProductImage = defineAsyncComponent(
  () => import('@/components/basic/atoms/ProductImage.vue')
)
const TheOpcAwards = defineAsyncComponent(
  () => import('@/components/opc/molecules/TheOpcAwards.vue')
)
const TheOpcStageInfo = defineAsyncComponent(
  () => import('@/components/opc/molecules/TheOpcStageInfo.vue')
)

const opcStore = useOpcStore()
const { calculated, params, updating, product } = storeToRefs(opcStore)
const { smAndDown, smAndUp, mdAndDown, mdAndUp, lgAndDown, lgAndUp } =
  useDisplay()

const wrapperRef = ref(null)

const badges = computed(() => params.value.badges ?? [])

const awardThreshold = computed(() => (smAndDown.value ? 2 : 3))

const coop = computed(() => params.value.coop)

const isBundle = computed(() => calculated.value.isBundle)

const image = computed(() =>
  smAndDown.value
    ? calculated.value.images.smallImage
    : calculated.value.images.image
)

const imageHeight = computed(() => {
  if (lgAndUp.value) return 550
  if (mdAndUp.value) return 480
  if (smAndUp.value) return 480
  return '100%'
})

const background = computed(() => {
  const {
    groups: { bg }
  } = location.pathname.match(/-bg-(?<bg>\d+)/) ?? { groups: { bg: 1 } }
  const number = bg > 1 && bg < 17 ? bg : 1

  let size
  switch (true) {
    case smAndDown.value:
      size = 'sm'
      break
    case mdAndDown.value:
      size = 'md'
      break
    case lgAndDown.value:
      size = 'lg'
      break
    default:
      size = 'xl'
  }
  const bgSource = new URL(
    `/src/assets/images/opc/backgrounds/bg_${number}_${size}.webp`,
    import.meta.url
  ).href

  return `background-image: url(${bgSource});`
})

const elbData = computed(() => ElbwalkerHelper.getProductData(product.value))

watch(product, async () => {
  await nextTick()
  if (wrapperRef.value) {
    window.elb('product visible', wrapperRef.value)
  }
})
</script>
<style scoped lang="scss">
.bg-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 50px;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
}

@media (min-width: 380px) and (max-width: 599px) {
  .bg-wrapper {
    bottom: 90px;
  }
}

.display-sm-and-up .bg-wrapper {
  bottom: 150px;
}

.display-lg-and-up .bg-wrapper {
  bottom: 160px;
}

.display-lg-and-up .wrapper {
  min-height: 650px;
}

.product-image {
  align-self: flex-end;
}

.loading-image {
  padding-bottom: 50px;
  padding-top: 75px;
}

.coop {
  position: fixed;
  bottom: 0;
  left: 0;
  height: 50px;
  z-index: 3;
  width: 100%;

  img {
    max-width: 130px;
  }
}

.display-md-and-up .coop {
  bottom: auto;
  top: 0;
  position: absolute;
  border-radius: 0 0 5px 5px;
  max-height: initial;
  width: auto;

  img {
    width: 100%;
  }
}
</style>
